import { FC } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import styled from '@emotion/styled';
import { apiMessagesStore } from '@api/utils/ApiMessagesStore';
import { DetailHeader } from '../common/DetailHeader';
import { FormContainer } from '../common/form/FormContainer';
import { Input } from '../common/form/Input';
import { useAppDispatch } from '../../store';
import { visitationListActions } from '../../store/entities/visitationList';
import { invitationListActions } from '../../store/entities/invitationList';
import { invitationTypeVisitListActions } from '../../store/entities/invitationTypeVisitList';
import { useNavigation } from '../../utils/urlPaths';
import { VisitationModeSelector } from './VisitationModeSelector';
import { VisitationEntryDto } from '@api/models/visitations/VisitationEntryDto';
import { visitationService } from '@api/services/services';
import { resources } from '@api/resources';
import { InvitedPersonDto } from '@api/models/invitation/InvitedPersonDto';
import { TrainingRunButton } from '../trainingRun/TrainingRunButton';
import { ShowResourceContent } from '../ShowResourceContent';
import { VisitorCard } from './VisitorCard';
import * as Yup from 'yup';
import { validations } from '../../utils/validations';
import { PersonRowState, SharedFormValues, useInvitationVisitData } from './useInvitationVisitData';

const SectionLabel = styled.h3(({ theme }) => ({
  margin: 0,
  fontSize: 14,
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  color: theme.palette.text.secondary,
}));

const CardList = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
});

export const VisitationVisitDetailForm: FC = () => {
  const { t, i18n } = useTranslation();
  const { goBack, getBackPath } = useNavigation('/visitation');
  const { invitationId } = useParams();
  const dispatch = useAppDispatch();

  const spzKey = `one_time_visit_temp_spz_${invitationId}`;
  const { title, entryEstablishmentId, rows, setRows, initialValues } = useInvitationVisitData(invitationId, spzKey);

  const allEntered = !!rows && rows.length > 0 && rows.every((r) => r.status === 'entered');

  const updateRow = (idx: number, patch: Partial<PersonRowState>) => setRows((prev) => prev?.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const handleEnter = async (rowIndex: number, values: SharedFormValues) => {
    const row = rows?.[rowIndex];
    if (!row || row.status === 'submitting' || row.status === 'entered' || !row.pin) return;

    updateRow(rowIndex, { status: 'submitting', errorMessage: undefined });

    const body: VisitationEntryDto = {
      invitation: invitationId!,
      entryEstablishment: entryEstablishmentId,
      pin: row.pin,
      spz: values.mode === 'car' ? values.spz : undefined,
      note: values.note,
    };

    try {
      await visitationService.visit(body);
      const time = new Date().toLocaleTimeString(i18n.language);
      updateRow(rowIndex, { status: 'entered', timestamp: time, errorMessage: undefined });

      const isLastPending = rows.every((r, i) => i === rowIndex || r.status === 'entered');
      if (isLastPending) {
        sessionStorage.removeItem(spzKey);
        dispatch(visitationListActions.reload(true));
        dispatch(invitationListActions.reload(true));
        dispatch(invitationTypeVisitListActions.reload(true));
        goBack();
      }
    } catch {
      const msg = apiMessagesStore.getErrorByResource(resources.visit);
      apiMessagesStore.clearError(resources.visit);
      updateRow(rowIndex, { status: 'error', errorMessage: msg ?? undefined });
    }
  };

  if (!rows || !initialValues) {
    return <ShowResourceContent resource={[resources.getInvitationById, resources.revealPin]}>{null}</ShowResourceContent>;
  }

  const notYetEnteredPersonsWithTraining: InvitedPersonDto[] = rows
    .filter((r) => r.status !== 'entered' && (r.person.requiredTrainings?.length ?? 0) > 0)
    .map((r) => r.person);

  return (
    <ShowResourceContent resource={[resources.getInvitationById, resources.revealPin]}>
      <DetailHeader title={title} backTo={getBackPath} />
      {!allEntered && (
        <Formik<SharedFormValues>
          initialValues={initialValues}
          validationSchema={Yup.object().shape({
            spz: Yup.string().when('mode', ([mode], schema) => (mode === 'car' ? validations.stringRequired(t) : schema.nullable())),
          })}
          onSubmit={() => undefined}
        >
          {({ values, validateForm, setFieldTouched }) => {
            const handleEnterClick = async (idx: number) => {
              const errors = await validateForm();
              if (Object.keys(errors).length > 0) {
                await Promise.all(Object.keys(errors).map((key) => setFieldTouched(key, true, false)));
                return;
              }
              handleEnter(idx, values);
            };

            return (
              <Form>
                <FormContainer>
                  <VisitationModeSelector />
                  <Input name="note" label={t('common.notes')} />

                  <SectionLabel>{t('visitation.oneTimeVisitors', { count: rows.length })}</SectionLabel>
                  <CardList>
                    {rows.map((row, idx) => {
                      const hasTraining = (row.person.requiredTrainings?.length ?? 0) > 0;
                      const personId = row.person.id ?? row.person.employeeId ?? String(idx);
                      return (
                        <VisitorCard
                          key={personId}
                          person={{
                            id: personId,
                            name: row.person.name,
                            lastName: row.person.lastName,
                            phone: row.person.phone,
                          }}
                          pin={row.pin}
                          status={row.status}
                          timestamp={row.timestamp}
                          errorMessage={row.errorMessage}
                          hasRequiredTraining={hasTraining}
                          onEnter={() => handleEnterClick(idx)}
                          trainingSlot={
                            hasTraining ? (
                              <TrainingRunButton
                                workers={row.person}
                                variant="outlined"
                                disabled={row.status === 'submitting' || row.status === 'entered'}
                              >
                                {t('visitation.oneTimeRunTraining')}
                              </TrainingRunButton>
                            ) : undefined
                          }
                        />
                      );
                    })}
                  </CardList>

                  {notYetEnteredPersonsWithTraining.length > 0 && (
                    <TrainingRunButton workers={notYetEnteredPersonsWithTraining}>{t('visitation.oneTimeRunGroupTraining')}</TrainingRunButton>
                  )}
                </FormContainer>
              </Form>
            );
          }}
        </Formik>
      )}
    </ShowResourceContent>
  );
};
